/* eslint-disable no-param-reassign */
import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AmplitudeService } from '@inyova/inyova-shared';
import { Customer, CustomerAccount } from '@inyova/models';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep } from 'lodash-es';
import { PageScrollService } from 'ngx-page-scroll-core';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import * as AppRootActions from '@app/app.actions';
import * as StrategyActions from '@strategy/strategy.actions';
import * as fromAppRoot from '@app/app.reducers';
import * as fromStrategy from '@strategy/reducers';

import { RISK_CONSTANTS } from '@app/app.constants';
import { RiskQuestionsSurveyOption, RiskSurveyQuestion } from '@shared/models/Strategy';
import { TrackingService } from '@shared/services/tracking.service';
import { isValidDate } from '@shared/utils';

type QuestionCategory = 'investmentHorizon' | 'expenses' | 'income' | 'riskTaking';

@Component({
  selector: 'app-setup-risk-3a',
  styleUrls: ['./setup-risk-3a.component.scss'],
  templateUrl: './setup-risk-3a.component.html',
  standalone: false
})
export class SetupRisk3AComponent implements OnDestroy, OnInit {
  @Input() recheck = false;
  @ViewChild('sliderWrapper') sliderWrapper: ElementRef;
  RETIREMENT_AGE = 65;
  protected readonly onDestroy$ = new Subject<void>();

  categories = {
    investmentHorizon: {
      collapsed: false,
      disabled: false
    },
    expenses: {
      collapsed: true,
      disabled: true
    },
    income: {
      collapsed: true,
      disabled: true
    },
    riskTaking: {
      collapsed: true,
      disabled: true
    },
    willingness: {
      collapsed: true,
      disabled: true
    }
  };

  customer: Customer;
  currentAccount: CustomerAccount;
  enableSaveButton = false;
  maxSliderValue = 0;
  wasChanged = false;
  RISK_CONSTANTS = RISK_CONSTANTS;
  withdrawalInfoOpen = false;
  invalidDate = '';
  riskQuestions: {
    investmentHorizon: RiskSurveyQuestion[];
    expenses: RiskSurveyQuestion[];
    income: RiskSurveyQuestion[];
    riskTaking: RiskSurveyQuestion[];
    willingness: RiskSurveyQuestion[];
  };

  sliderValue = '0%';
  minSliderValue = 0;
  trustQuestion = false;
  increasedRiskAcknowledgement = false;
  isLoading: boolean;
  insufficient: boolean;
  showIncreasedRiskMessage = false;
  stockSplitPicked: number;
  stockSplit: number;
  maxStockSplit: number;
  stockRatio: number;
  bondRatio: number;
  recommendedClass: string;
  riskName = 'balanced';
  pickedValue: number;
  savedDOB: string[] = ['', '', ''];
  riskAnswersCopy = [];
  savedRiskAnswers = [];
  startAmplitudeSubmitted: boolean;

  constructor(
    private router: Router,
    private store: Store,
    private trackingService: TrackingService,
    private translateService: TranslateService,
    private scrollService: PageScrollService,
    private amplitudeService: AmplitudeService,
    @Inject(DOCUMENT) private document: any
  ) {
    this.startAmplitudeSubmitted = false;

    this.store.pipe(select(fromAppRoot.selectCustomer), takeUntil(this.onDestroy$)).subscribe((customer) => {
      this.customer = customer;
    });

    this.store
      .pipe(
        select(fromAppRoot.selectLanguageChange),
        filter((isLanguageChange) => isLanguageChange === true),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => {
        this.store.dispatch(StrategyActions.getRiskStockSplitSuccess({ riskStockSplit: null, maxStockSplit: null }));
        this.store.dispatch(StrategyActions.getRiskQuestions());
        this.store.dispatch(AppRootActions.setIsLanguageChange({ change: false }));
      });

    this.store.pipe(select(fromAppRoot.selectCurrentInvestAccount), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.currentAccount = value;

      if (value && value.step_status !== 1) {
        this.trustQuestion = true;
      }
    });
    this.store.pipe(select(fromStrategy.selectRiskLoader), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.isLoading = value;
    });
    this.store.pipe(select(fromStrategy.selectRiskStockSplit), takeUntil(this.onDestroy$)).subscribe(({ riskStockSplit, maxStockSplit }) => {
      if (riskStockSplit === null) {
        return;
      }

      this.minSliderValue = riskStockSplit === 0 ? 0 : 1;
      this.stockSplit = riskStockSplit;
      this.maxStockSplit = maxStockSplit;

      if (this.riskQuestions && this.riskQuestions.willingness.length && this.wasChanged) {
        const answered = this.riskQuestions.willingness[0].survey_answer_options.find((item) => item.weight === this.stockSplit);
        this.updateWillingnessQuestion({ target: { value: (answered.score + 3).toString() } });
      }
      this.getRecommendedClass(this.stockSplit);
    });
    this.store.pipe(select(fromStrategy.select3ARiskQuestionsSorted), takeUntil(this.onDestroy$)).subscribe((questions) => {
      // Create a new reference on all levels of the object
      this.riskQuestions = cloneDeep(questions);
      // Update investmentHorizon questions
      const dateQuestion = this.riskQuestions.investmentHorizon?.[0];
      const ageQuestion = this.riskQuestions.investmentHorizon?.[1];
      if (dateQuestion && dateQuestion.survey_customer_answer && dateQuestion.survey_customer_answer.answer) {
        const answer = dateQuestion.survey_customer_answer.answer as string;
        this.updateDOB(answer.split('/'));
        dateQuestion.hasAnswer = true;
        dateQuestion.question_text = this.replaceQuestionText(
          dateQuestion,
          {
            option_text: `${this.formatDOBItem(this.savedDOB[2])}/${this.formatDOBItem(this.savedDOB[1])}/${this.savedDOB[0]}`
          } as RiskQuestionsSurveyOption,
          false
        );
        this.getMaxSliderValue(answer, ageQuestion.survey_customer_answer.answer as number);
      }
      if (ageQuestion && ageQuestion.survey_customer_answer && ageQuestion.survey_customer_answer.answer) {
        ageQuestion.hasAnswer = true;
        ageQuestion.question_text = this.replaceQuestionText(
          ageQuestion,
          { option_text: ageQuestion.survey_customer_answer.answer } as RiskQuestionsSurveyOption,
          false
        );
        this.getSliderValue(ageQuestion.survey_customer_answer.answer as string);
      }
      // Update expenses questions
      this.riskQuestions.expenses.forEach((question: RiskSurveyQuestion) => this.initialiseAnswersAfterLoad(question, 'expenses'));
      // Update income questions
      this.riskQuestions.income.forEach((question: RiskSurveyQuestion) => this.initialiseAnswersAfterLoad(question, 'income'));
      // Update riskTaking questions
      this.riskQuestions.riskTaking.forEach((question: RiskSurveyQuestion) => this.initialiseAnswersAfterLoad(question, 'riskTaking'));
      // Update willingness question
      const willingnessQuestion = this.riskQuestions.willingness[0];
      if (willingnessQuestion && willingnessQuestion.survey_customer_answer && willingnessQuestion.survey_customer_answer.answer) {
        const answered = willingnessQuestion.survey_answer_options.find((item) => item.id === willingnessQuestion.survey_customer_answer.answer);
        this.pickedValue = answered.score + 3;
        this.stockSplitPicked = answered.weight;
        this.bondRatio = (1 - answered.weight) * 100;
        this.stockRatio = answered.weight * 100;
        this.getRiskName();
      }
      this.riskAnswersCopy = cloneDeep(this.getRiskAnswers());
      this.savedRiskAnswers = this.riskAnswersCopy.map((answer) => answer.answer);

      this.enableNextCategory();
      this.getRiskStockSplit();
      this.enableSaveButtonCheck();

      if (this.startAmplitudeSubmitted) {
        return;
      }

      const payload = {
        placement: this.currentAccount.step_status < 5 ? 'onboarding' : 'strategy_configuration',
        offering_account_type: this.currentAccount.kind
      };

      this.startAmplitudeSubmitted = true;

      this.amplitudeService.trackEvent('Risk Profile Adjust Started', payload);
    });
  }

  ngOnInit(): void {
    this.store
      .pipe(
        select(fromStrategy.select3ARiskIsInitialized),
        filter((isInitialized) => isInitialized === false),
        takeUntil(this.onDestroy$)
      )
      .subscribe((isInitialized) => {
        this.store.dispatch(StrategyActions.getRiskStockSplitSuccess({ riskStockSplit: null, maxStockSplit: null }));
        this.store.dispatch(StrategyActions.getRiskQuestions());
      });

    if (this.recheck) {
      this.trackingService.trackView('/risk-recheck');
    } else {
      this.trackingService.trackView('/risk-profile');
    }
  }

  answerQuestion(question: RiskSurveyQuestion, answer: RiskQuestionsSurveyOption, category: QuestionCategory): void {
    const questionCopy = {
      ...question,
      hasAnswer: true,
      question_text: this.replaceQuestionText(question, answer, category === 'riskTaking'),
      survey_customer_answer: {
        answer: answer.id
      }
    };

    const index = this.riskQuestions[category].findIndex((item) => item.id === question.id);
    this.riskQuestions[category][index] = questionCopy;
    this.enableNextCategory();
    this.enableSaveButtonCheck();
    this.getRiskStockSplit();
  }

  trackTooltip(name: string) {
    this.amplitudeService.trackEvent('Tooltip Info Consulted', {
      tooltip_name: name,
      screen: '3a setup risk',
      consultation_method: 'hover'
    });
  }

  getRetirmentAge(question: RiskSurveyQuestion): number {
    if (!question || !question.hasAnswer) {
      return 0;
    }
    let date = new Date();
    date = new Date(date.getFullYear() + Number(question.survey_customer_answer.answer), date.getMonth(), date.getDate());
    return date.getFullYear() - Number(this.savedDOB[0]);
  }

  getSliderValue(value: string): string {
    if (!value || !this.maxSliderValue) {
      this.sliderValue = '0%';
    }
    const difference = (Number(value) - 1) / (this.maxSliderValue - 1);
    if (difference >= 0 && difference <= 1) {
      this.sliderValue = `${difference * 100}%`;
    }
    return this.sliderValue;
  }

  getRiskFormData() {
    return {
      risk_answers: this.getRiskAnswers(),
      selected_stock_split: this.stockSplitPicked
    };
  }

  continue(): void {
    this.store.dispatch(StrategyActions.navigateFromRiskPageToStrategyReview(this.getRiskFormData()));
  }

  send(): void {
    this.store.dispatch(StrategyActions.updateRiskEve2(this.getRiskFormData()));
  }

  ngOnDestroy(): void {
    this.startAmplitudeSubmitted = false;
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  updateDOB(dob: string[]): void {
    this.savedDOB = dob;

    if (this.savedDOB[1].length && this.savedDOB[2].length && this.savedDOB[0].length > 3) {
      const date = new Date(this.savedDOB.join('/'));
      const index = this.riskQuestions.investmentHorizon.findIndex((item) => item.inyova_id === RISK_CONSTANTS.INVESTMENT_HORIZON_FIRST_QUESTION_ID);
      let overTheAgeLimit = true;
      let bellowTheAgeLimit = true;

      if (date && isValidDate(this.savedDOB)) {
        overTheAgeLimit = new Date(date.getFullYear() + this.RETIREMENT_AGE - 1, date.getMonth(), date.getDate()) < new Date();
        bellowTheAgeLimit = new Date(date.getFullYear() + 18, date.getMonth(), date.getDate()) > new Date();
      }

      if (!overTheAgeLimit && !bellowTheAgeLimit) {
        this.invalidDate = '';
        this.riskQuestions.investmentHorizon[index] = {
          ...this.riskQuestions.investmentHorizon[index],
          hasAnswer: true,
          question_text: this.replaceQuestionText(this.riskQuestions.investmentHorizon[index], {
            option_text: `${this.formatDOBItem(this.savedDOB[2])}/${this.formatDOBItem(this.savedDOB[1])}/${this.savedDOB[0]}`
          } as RiskQuestionsSurveyOption),
          survey_customer_answer: {
            answer: this.savedDOB.join('/')
          }
        };
        this.getMaxSliderValue(this.savedDOB.join('/'));
        this.getRiskStockSplit();
      } else {
        const indexHorizon = this.riskQuestions.investmentHorizon.findIndex(
          (item) => item.inyova_id === RISK_CONSTANTS.INVESTMENT_HORIZON_SECOND_QUESTION_ID
        );
        this.maxSliderValue = 0;
        this.riskQuestions.investmentHorizon[index] = {
          ...this.riskQuestions.investmentHorizon[index],
          hasAnswer: false,
          question_text: this.replaceQuestionText(this.riskQuestions.investmentHorizon[index], {
            option_text: `______`
          } as RiskQuestionsSurveyOption),
          survey_customer_answer: null
        };
        this.riskQuestions.investmentHorizon[indexHorizon] = {
          ...this.riskQuestions.investmentHorizon[indexHorizon],
          hasAnswer: false,
          question_text: this.replaceQuestionText(this.riskQuestions.investmentHorizon[indexHorizon], {
            option_text: `______`
          } as RiskQuestionsSurveyOption),
          survey_customer_answer: null
        };
        if (bellowTheAgeLimit && overTheAgeLimit) {
          this.invalidDate = this.translateService.instant('new_strategy.risk_screen.3a.investmentHorizon.error.invalidBirthdate');
        } else if (bellowTheAgeLimit) {
          this.invalidDate = this.translateService.instant('new_strategy.risk_screen.3a.investmentHorizon.error.belowAgeLimit');
        } else if (overTheAgeLimit) {
          this.invalidDate = this.translateService.instant('new_strategy.risk_screen.3a.investmentHorizon.error.aboveAgeLimit', {
            max3aAge: this.RETIREMENT_AGE
          });
        }
      }
      this.enableNextCategory();
      this.enableSaveButtonCheck();
    }
  }

  getRecommendedClass(stockSplit: number): void {
    switch (true) {
      case stockSplit >= 1:
        this.recommendedClass = 'very_dynamic';
        break;
      case stockSplit >= 0.8:
        this.recommendedClass = 'dynamic';
        break;
      case stockSplit >= 0.6:
        this.recommendedClass = 'balanced';
        break;
      case stockSplit >= 0.4:
        this.recommendedClass = 'conservative';
        break;
      case stockSplit >= 0:
        this.recommendedClass = 'very_conservative';
        break;
      default:
        this.recommendedClass = 'active';
        break;
    }
  }

  enableSaveButtonCheck(): void {
    this.enableSaveButton =
      !this.categories.expenses.disabled &&
      !this.categories.income.disabled &&
      !this.categories.riskTaking.disabled &&
      !this.categories.willingness.disabled &&
      !this.invalidDate.length &&
      this.stockSplitPicked !== 0 &&
      this.trustQuestion;
    if (this.showIncreasedRiskMessage) {
      this.enableSaveButton = this.enableSaveButton && this.increasedRiskAcknowledgement;
    }
    this.wasChanged = JSON.stringify(this.getRiskAnswers()) !== JSON.stringify(this.riskAnswersCopy);

    this.store.dispatch(StrategyActions.setStrategyWasChangedEve2({ strategyWasChanged: this.wasChanged }));
  }

  updateInvestmentHorizon(event: { target: { value: string } }, question: RiskSurveyQuestion): void {
    const { value } = event.target;
    const questionCopy = {
      ...question,
      question_text: this.replaceQuestionText(question, {
        option_text: `${value} ${this.translateService.instant('strategy.years') as string}`
      } as RiskQuestionsSurveyOption),
      survey_customer_answer: {
        answer: value
      },
      hasAnswer: true
    };
    const index = this.riskQuestions.investmentHorizon.findIndex((item) => item.id === question.id);
    this.riskQuestions.investmentHorizon[index] = questionCopy;
    this.getSliderValue(value);
    this.enableNextCategory();
    this.enableSaveButtonCheck();
    this.getRiskStockSplit();
  }

  updateWillingnessQuestion(event: { target: { value: string } }): void {
    const { value } = event.target as HTMLInputElement;
    const answered = this.riskQuestions.willingness[0].survey_answer_options[value] as RiskQuestionsSurveyOption;
    const questionCopy = {
      ...this.riskQuestions.willingness[0],
      survey_customer_answer: {
        answer: answered.id
      }
    };
    this.pickedValue = answered.score + 3;
    this.stockSplitPicked = answered.weight;
    this.bondRatio = (1 - answered.weight) * 100;
    this.stockRatio = answered.weight * 100;
    this.riskQuestions.willingness[0] = questionCopy;
    this.showIncreasedRiskMessage = this.stockSplitPicked > this.maxStockSplit;
    this.getRiskName();
    this.enableSaveButtonCheck();
    this.isInsufficient();
  }

  toggleCategory(categoryName: QuestionCategory): void {
    this.categories[categoryName].collapsed = !this.categories[categoryName].collapsed;
  }

  private areAllQuestionsAnswered(questions: RiskSurveyQuestion[]): boolean {
    const filter = questions.filter((item) => item.survey_customer_answer === null);
    return !!filter.length;
  }

  private getMaxSliderValue(value: string, savedValue?: number): void {
    const date = new Date(value);
    const difference = new Date().getFullYear() - date.getFullYear();
    if (difference > 0) {
      this.maxSliderValue = this.RETIREMENT_AGE - difference;
      if (!savedValue) {
        this.updateInvestmentHorizon(
          { target: { value: `${this.maxSliderValue >= 0 ? this.maxSliderValue : 0}` } },
          this.riskQuestions.investmentHorizon[1]
        );
      }
    }
  }

  private getRiskName(): void {
    switch (true) {
      case this.stockSplitPicked >= 1:
        this.riskName = 'very_dynamic';
        break;
      case this.stockSplitPicked >= 0.8:
        this.riskName = 'dynamic';
        break;
      case this.stockSplitPicked >= 0.6:
        this.riskName = 'balanced';
        break;
      case this.stockSplitPicked >= 0.4:
        this.riskName = 'conservative';
        break;
      case this.stockSplitPicked >= 0.2:
        this.riskName = 'very_conservative';
        break;
      case this.stockSplitPicked >= 0:
        this.riskName = 'low_risk';
        break;
      default:
        this.riskName = 'balanced';
        break;
    }
  }

  private enableNextCategory(): void {
    // Expenses
    this.categories.expenses.disabled = this.areAllQuestionsAnswered(this.riskQuestions.investmentHorizon);
    this.categories.expenses.collapsed = this.categories.expenses.disabled;

    // Income
    this.categories.income.disabled = this.areAllQuestionsAnswered(this.riskQuestions.expenses);
    this.categories.income.collapsed = this.categories.income.disabled;

    // Risk
    this.categories.riskTaking.disabled = this.areAllQuestionsAnswered(this.riskQuestions.income);
    this.categories.riskTaking.collapsed = this.categories.riskTaking.disabled;

    // Willingess
    this.categories.willingness.disabled = this.areAllQuestionsAnswered(this.riskQuestions.riskTaking);
    this.categories.willingness.collapsed = this.categories.willingness.disabled;

    setTimeout(() => {
      if (this.sliderWrapper && !this.recheck) {
        this.scrollService.scroll({
          document: this.document,
          scrollTarget: this.sliderWrapper.nativeElement
        });
      }
    }, 300);
  }

  private replaceQuestionText(question: RiskSurveyQuestion, answer: RiskQuestionsSurveyOption, appendToEnd = false): string {
    const questionText = question.question_text;
    if (questionText.includes('______')) {
      return questionText.replace('______', `<b>${answer.option_text}</b>`);
    }
    if (questionText.includes('<b>')) {
      const reg = /(?:<b>)([^#]+)(?=<\/b>*)/;
      return questionText.replace(reg, `<b>${answer.option_text}</b>`);
    }
    if (appendToEnd) {
      return `${questionText} <b>${answer.option_text}</b>`;
    }
    return questionText;
  }

  private initialiseAnswersAfterLoad(question: RiskSurveyQuestion, category: string) {
    if (question && question.survey_customer_answer && question.survey_customer_answer.answer) {
      const answer = question.survey_answer_options.find((questionItem) => questionItem.id === question.survey_customer_answer.answer);
      question.hasAnswer = true;
      if (answer) {
        question.question_text = this.replaceQuestionText(question, answer, category === 'riskTaking');
      }
    }
  }

  private getRiskAnswers(): { question_id: number; answer: number | string }[] {
    const riskAnswers: { question_id: number; answer: number | string }[] = [];
    Object.entries(this.riskQuestions || {}).forEach(([_, questions]) => {
      if (Array.isArray(questions)) {
        questions.forEach((question) => {
          const answer = question?.survey_customer_answer?.answer;

          if (question?.id && answer != null) {
            riskAnswers.push({
              question_id: question.id,
              answer: Array.isArray(answer) ? answer[0] : answer
            });
          }
        });
      }
    });
    return riskAnswers;
  }

  private getRiskStockSplit(): void {
    if (
      !this.categories.investmentHorizon.disabled &&
      !this.categories.expenses.disabled &&
      !this.categories.income.disabled &&
      !this.categories.riskTaking.disabled &&
      !this.categories.willingness.disabled
    ) {
      const riskAnswers = this.getRiskAnswers();
      if (riskAnswers.length > 0) {
        this.store.dispatch(StrategyActions.getRiskStockSplit({ riskAnswers }));
      }
    }
  }

  private formatDOBItem(item: string): string {
    if (item.length === 2) {
      return item;
    }
    return `0${item}`;
  }

  sendRecheck(): void {
    this.store.dispatch(StrategyActions.updateRiskRecheckEve2(this.getRiskFormData()));
  }

  discard(): void {
    this.store.dispatch(StrategyActions.discardRiskRecheckEve2());
  }

  isInsufficient(): void {
    this.insufficient = this.stockSplitPicked > this.maxStockSplit;
    this.enableSaveButton = this.enableSaveButton && !this.insufficient;
  }
}
